import React, { useMemo } from 'react'
import { Bar } from 'react-chartjs-2'
import { Card, CardContent } from '@mui/material'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { hasChartData } from './PieChart'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

interface BarChartData {
  className?: string
  labels: string[]
  datasets: {
    label: string
    data: number[]
    backgroundColor: string | string[]
    borderColor?: string | string[]
    borderWidth?: number
  }[]
}

interface BarChartProps {
  title: string
  chartTitle?: string
  className?: string
  data: BarChartData
  /** Render chart only (no MUI Card / title) — for ma-card shells */
  embedded?: boolean
}

function asColorArray(color: string | string[] | undefined, len: number): string[] {
  if (Array.isArray(color)) return color
  if (typeof color === 'string') return Array(len).fill(color)
  return Array(len).fill('#8a9ba7')
}

const BarChart: React.FC<BarChartProps> = ({
  title,
  data,
  className,
  embedded = false,
}) => {
  const hasData = hasChartData(data)
  const isMultiSeries = (data?.datasets?.length || 0) > 1
  const categoryCount = data?.labels?.length || 0

  // Cap bar width so a single category doesn't stretch edge-to-edge
  const maxBarThickness =
    categoryCount <= 1 ? 56 : categoryCount <= 3 ? 48 : categoryCount <= 6 ? 40 : 32

  const legendRows = useMemo(() => {
    if (!hasData || !data?.datasets?.length) return []
    if (isMultiSeries) {
      return data.datasets.map((ds) => ({
        label: ds.label,
        color: asColorArray(ds.backgroundColor, 1)[0],
        value: ds.data.reduce((s, v) => s + (Number(v) || 0), 0),
      }))
    }
    const colors = asColorArray(data.datasets[0].backgroundColor, categoryCount)
    return data.labels.map((label, i) => ({
      label,
      color: colors[i],
      value: Number(data.datasets[0].data[i]) || 0,
    }))
  }, [data, hasData, isMultiSeries, categoryCount])

  if (!hasData) return null

  const total = legendRows.reduce((s, r) => s + r.value, 0)

  const chartBody = embedded ? (
    <div className={`ma-bar-layout${isMultiSeries ? ' is-multi' : ''}`}>
      <div className="ma-chart-canvas ma-chart-canvas--bar">
        <Bar
          data={data}
          options={{
            maintainAspectRatio: false,
            responsive: true,
            datasets: {
              bar: {
                maxBarThickness,
                categoryPercentage: categoryCount <= 2 ? 0.45 : 0.65,
                barPercentage: 0.75,
                borderRadius: 6,
                borderSkipped: false,
              },
            },
            plugins: {
              legend: { display: false },
              tooltip: { enabled: true },
            },
            scales: {
              x: {
                ticks: {
                  display: true,
                  color: '#5f7484',
                  font: { size: 11, family: 'Lato, sans-serif', weight: 600 },
                  maxRotation: categoryCount > 4 ? 25 : 0,
                  autoSkip: true,
                },
                grid: { display: false },
              },
              y: {
                beginAtZero: true,
                ticks: {
                  display: true,
                  color: '#8a9ba7',
                  font: { size: 11, family: 'Lato, sans-serif' },
                  precision: 0,
                  stepSize: 1,
                },
                grid: { color: '#eaf0f0' },
                suggestedMax:
                  Math.max(...data.datasets.flatMap((d) => d.data.map(Number))) <= 1
                    ? 2
                    : undefined,
              },
            },
          }}
        />
      </div>
      <ul className="ma-bar-legend">
        {legendRows.map((row) => {
          const active = row.value > 0
          const pct = total > 0 ? Math.round((row.value / total) * 100) : 0
          return (
            <li
              key={row.label}
              className={`ma-bar-legend-item${active ? '' : ' is-muted'}`}
            >
              <span
                className="ma-bar-legend-swatch"
                style={{ background: row.color }}
              />
              <span className="ma-bar-legend-label" title={row.label}>
                {row.label}
              </span>
              <span className="ma-bar-legend-value">
                {active ? (isMultiSeries ? row.value : `${row.value} · ${pct}%`) : '—'}
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  ) : (
    <>
      <div className="ma-chart-canvas ma-chart-canvas--bar">
        <Bar
          data={data}
          options={{
            maintainAspectRatio: false,
            responsive: true,
            datasets: {
              bar: {
                maxBarThickness,
                categoryPercentage: categoryCount <= 2 ? 0.45 : 0.65,
                barPercentage: 0.75,
                borderRadius: 6,
              },
            },
            plugins: {
              legend: { display: false },
              tooltip: { enabled: true },
            },
            scales: {
              x: {
                ticks: {
                  display: true,
                  color: '#5f7484',
                  font: { size: 10, family: 'Lato, sans-serif' },
                },
                grid: { display: false },
              },
              y: {
                beginAtZero: true,
                ticks: {
                  display: true,
                  color: '#8a9ba7',
                  font: { size: 10, family: 'Lato, sans-serif' },
                  precision: 0,
                },
                grid: { color: '#eaf0f0' },
              },
            },
          }}
        />
      </div>
    </>
  )

  if (embedded) {
    return <div className={className}>{chartBody}</div>
  }

  return (
    <Card
      style={{
        height: '100%',
        width: '100%',
        maxWidth: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
      className={className}
    >
      <CardContent
        style={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}
      >
        <h5 className="text-center" style={{ height: '50px' }}>
          {title}
        </h5>
        {chartBody}
      </CardContent>
    </Card>
  )
}

export default BarChart
